define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Enum/EnumTimeIntervalType'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    EnumTimeIntervalType
) {
    'use strict'
    Element.prototype.TimeInstance = null
    
    var Time = (() => {
        return function (timeCallback) {
            if (Element.prototype.TimeInstance) {
                return Element.prototype.TimeInstance
            }
            // 如果传入了回调函数，设置为静态回调
            if (timeCallback && typeof timeCallback === 'function') {
                Time.callback = timeCallback;
            }
            return Element.prototype.TimeInstance = new TimeSingleton()
        }
    })()
    
    // 初始化静态回调函数属性
    Time.callback = null;
    
    // 记录当前时区偏移量（小时）
    Time._currentTimezone = 0;
    
    /**
     * 设置时间播放的倍率。
     * 
     * @function setTimeScale
     * @memberof Time
     * @static
     * 
     * @param {Number} scale 时间倍率。例如：1.0为正常速度，2.0为双倍速度，0.5为半倍速度。
     * 
     * @example
     * // 设置双倍速播放
     * CooWasm.Time.setTimeScale(2.0);
     * 
     * // 设置半倍速播放
     * CooWasm.Time.setTimeScale(0.5);
     */
    Time.setTimeScale = function(scale) {
        Element.prototype.NativeAPI.SetTimeScale(scale)
    }
    
    /**
     * 设置时区偏移量。
     * 
     * @function setTimeZone
     * @memberof Time
     * @static
     * 
     * @param {Number|String} timezone 时区偏移量或时区字符串。
     *                                  数字：相对于UTC的小时偏移量，例如8表示UTC+8，-5表示UTC-5。
     *                                  字符串：支持多种格式如"GMT+8"、"UTC-5:30"、"+08:00"、"+0800"等。
     * 
     * @example
     * // 设置东八区（北京时间）
     * CooWasm.Time.setTimeZone(8);
     * 
     * // 使用字符串格式设置时区
     * CooWasm.Time.setTimeZone("GMT+8");
     * CooWasm.Time.setTimeZone("UTC-5:30");
     * CooWasm.Time.setTimeZone("+08:00");
     */
    Time.setTimeZone = function(timezone) {
        let offset = 0

        if (typeof timezone === 'number') {
            offset = timezone;
        } else if (typeof timezone === 'string') {
            // 移除空格并转为大写
            const tz = timezone.replace(/\s/g, '').toUpperCase()

            // 匹配各种格式的时区字符串
            const patterns = [
                /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/,     // GMT+8, GMT-5:30
                /^UTC([+-])(\d{1,2})(?::?(\d{2}))?$/,     // UTC+8, UTC-5:30
                /^([+-])(\d{1,2})(?::?(\d{2}))?$/,        // +08:00, -05:30
                /^([+-])(\d{4})$/                          // +0800, -0530
            ]

            for (const pattern of patterns) {
                const match = tz.match(pattern);
                if (match) {
                    const sign = match[1] === '+' ? 1 : -1
                    const hours = parseInt(match[2], 10)
                    let minutes = 0;

                    if (match[3]) {
                        minutes = parseInt(match[3], 10)
                    } else if (pattern === patterns[3]) {
                        // 处理 +0800 格式
                        const timeStr = match[2]
                        minutes = parseInt(timeStr.slice(-2), 10)
                    }

                    offset = sign * (hours + minutes / 60)
                    break
                }
            }
        }
        Element.prototype.NativeAPI.SetTimeZone(offset)
        // 记录当前时区设置
        Time._currentTimezone = offset;
    }
    
    /**
     * 设置当前时间。
     * 
     * @function setTime
     * @memberof Time
     * @static
     * 
     * @param {Date|Number|String} timeValue 要设置的时间值。
     *                                       Date对象：JavaScript Date对象。
     *                                       数字：时间戳（毫秒或秒，自动识别）。
     *                                       字符串：各种时间格式字符串，包括RFC1123、ISO8601等。
     * @param {Boolean} [preserveTimezone=true] 是否保持当前设置的时区。为true时，会将输入时间
     *                                          按照当前时区进行调整；为false时，直接使用输入时间的时区信息。
     * 
     * @example
     * // 使用Date对象设置时间（保持当前时区）
     * CooWasm.Time.setTime(new Date());
     * 
     * // 使用时间戳设置时间
     * CooWasm.Time.setTime(1640995200000); // 毫秒时间戳
     * CooWasm.Time.setTime(1640995200);    // 秒时间戳
     * 
     * // 使用字符串设置时间（自动应用当前时区）
     * CooWasm.Time.setTime("2022-01-01 12:00:00");
     * 
     * // 直接使用带时区信息的字符串（不调整时区）
     * CooWasm.Time.setTime("Sat, 01 Jan 2022 12:00:00 GMT", false);
     */
    Time.setTime = function(timeValue, preserveTimezone = true) {
        let rfc1123String = ''
        let targetDate = null;

        if (timeValue instanceof Date) {
            // Date对象处理
            targetDate = new Date(timeValue.getTime());
        } else if (typeof timeValue === 'number') {
            // 时间戳处理
            let timestamp = timeValue
            // 如果是秒级时间戳，转换为毫秒
            if (timestamp < 10000000000) {
                timestamp *= 1000
            }
            targetDate = new Date(timestamp);
        } else if (typeof timeValue === 'string') {
            // 字符串格式处理
            const trimmedValue = timeValue.trim()

            // 检查是否已经是RFC1123格式
            const rfc1123Pattern = /^[A-Za-z]{3},\s\d{1,2}\s[A-Za-z]{3}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT$/;
            if (rfc1123Pattern.test(trimmedValue)) {
                // 已经是RFC1123格式，直接使用
                rfc1123String = trimmedValue;
                Element.prototype.NativeAPI.SetTime(rfc1123String);
                return;
            } else {
                // 尝试解析其他格式
                targetDate = new Date(trimmedValue);
                if (isNaN(targetDate.getTime())) {
                    console.error('Invalid time format:', timeValue);
                    return;
                }
            }
        } else {
            console.error('Unsupported time value type:', typeof timeValue);
            return;
        }

        // 如果preserveTimezone为true且设置了时区，需要进行时区调整
        if (preserveTimezone && Time._currentTimezone !== 0) {
            // 调整到目标时区
            const timezoneOffsetMinutes = Time._currentTimezone * 60;
            const adjustedTime = new Date(targetDate.getTime() + timezoneOffsetMinutes * 60 * 1000);
            
            // 生成带时区信息的RFC1123格式字符串
            rfc1123String = formatDateToRFC1123WithTimezone(adjustedTime, Time._currentTimezone);
        } else {
            // 不调整时区，直接转换为UTC格式
            rfc1123String = targetDate.toUTCString();
        }
        
        Element.prototype.NativeAPI.SetTime(encode(rfc1123String));
    }
    
    /**
     * 将Date对象格式化为带时区信息的RFC1123格式字符串
     * @private
     */
    function formatDateToRFC1123WithTimezone(date, timezoneOffset) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const dayName = days[date.getUTCDay()];
        const day = String(date.getUTCDate()).padStart(2, '0');
        const monthName = months[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        
        // 生成时区字符串
        let timezoneStr;
        if (timezoneOffset === 0) {
            timezoneStr = 'GMT';
        } else {
            const sign = timezoneOffset >= 0 ? '+' : '-';
            const absOffset = Math.abs(timezoneOffset);
            const offsetHours = Math.floor(absOffset);
            const offsetMinutes = Math.round((absOffset - offsetHours) * 60);
            
            if (offsetMinutes === 0) {
                timezoneStr = `GMT${sign}${String(offsetHours).padStart(2, '0')}00`;
            } else {
                timezoneStr = `GMT${sign}${String(offsetHours).padStart(2, '0')}${String(offsetMinutes).padStart(2, '0')}`;
            }
        }
        
        return `${dayName}, ${day} ${monthName} ${year} ${hours}:${minutes}:${seconds} ${timezoneStr}`;
    }
    
    /**
     * 获取当前时间。
     * 
     * @function getTime
     * @memberof Time
     * @static
     * 
     * @param {String} [format='date'] 返回时间的格式。
     *                                 'date'：返回JavaScript Date对象（默认）。
     *                                 'timestamp'：返回毫秒时间戳。
     *                                 'rfc1123'：返回RFC1123格式字符串。
     *                                 'iso'：返回ISO8601格式字符串。
     * 
     * @returns {Date|Number|String} 根据format参数返回不同格式的时间。
     * 
     * @example
     * // 获取Date对象
     * var date = CooWasm.Time.getTime();
     * var date = CooWasm.Time.getTime('date');
     * 
     * // 获取时间戳
     * var timestamp = CooWasm.Time.getTime('timestamp');
     * 
     * // 获取RFC1123格式字符串
     * var rfc1123 = CooWasm.Time.getTime('rfc1123');
     * 
     * // 获取ISO格式字符串
     * var iso = CooWasm.Time.getTime('iso');
     */
    Time.getTime = function(format = 'date') {
        const rfc1123String = Element.prototype.NativeAPI.GetTime()
        const date = new Date(rfc1123String)

        switch (format) {
            case 'timestamp':
                return date.getTime()
            case 'rfc1123':
                return rfc1123String
            case 'iso':
                return date.toISOString()
            case 'date':
            default:
                return date
        }
    }
    
    /**
     * 暂停时间播放。
     * 
     * @function pause
     * @memberof Time
     * @static
     * 
     * @example
     * // 暂停时间播放
     * CooWasm.Time.pause();
     */
    Time.pause = function() {
        Element.prototype.NativeAPI.ControlTime(1)
    }
    
    /**
     * 恢复时间播放。
     * 
     * @function resume
     * @memberof Time
     * @static
     * 
     * @example
     * // 恢复时间播放
     * CooWasm.Time.resume();
     */
    Time.resume = function() {
        Element.prototype.NativeAPI.ControlTime(2)
    }
    
    /**
     * 重置时间控制器到初始状态。
     * 
     * @function reset
     * @memberof Time
     * @static
     * 
     * @example
     * // 重置时间控制器
     * CooWasm.Time.reset();
     */
    Time.reset = function() {
        Element.prototype.NativeAPI.ControlTime(0)
    }
    
    /**
     * 设置时间回调的间隔。
     * 
     * @function setTimeInterval
     * @memberof Time
     * @static
     * 
     * @param {Number} second 间隔时间，单位：秒。小于0为不触发回调，0为每帧触发回调
     * 
     * @example
     * // 设置每秒触发一次时间回调
     * CooWasm.Time.setTimeInterval(1);
     * 
     * // 设置不触发回调
     * CooWasm.Time.setTimeInterval(-1);
     * 
     * // 每帧触发回调
     * CooWasm.Time.setTimeInterval(0);
     */
    Time.setTimeInterval = function(second) {
        Element.prototype.NativeAPI.SetTimeIntervalType(second)
    }
    /**
     * 获取时间回调的间隔。
     * 
     * @function getTimeInterval
     * @memberof Time
     * @static
     * 
     * @returns {Number} 时间回调的间隔，单位：秒。小于0为不触发回调，0为每帧触发回调
     */
    Time.getTimeInterval = function () {
        let time = Element.prototype.NativeAPI.GetTimeInterval()
        return time
    }

    /**
     * 时间控制器。
     * 
     * @demo {@link http://lbs.zjkelan.com/Shadow.html|CooWasm Shadow demo}
     * @alias Time
     * @constructor
     *
     * @param {CallbackString} [timeCallback=null] 时间信息的函数回调。
     * 
     * @property {Function} callback 静态回调函数属性，用于接收时间更新通知。
     * 
     * @example
     * // 创建时间控制器实例并传入回调函数
     * var time = new CooWasm.Time((timeString) => {
     *     console.log("time:", timeString);
     * });
     * 
     * // 或者使用静态方式设置回调:
     * CooWasm.Time.callback = (timeString) => {
     *     console.log("time:", timeString);
     * };
     * var time = new CooWasm.Time();
     * 
     * // 静态方法使用示例:
     * CooWasm.Time.setTimeScale(2.0);           // 设置时间倍率
     * CooWasm.Time.setTimeZone(8);              // 设置时区
     * CooWasm.Time.setTime(new Date());         // 设置时间
     * CooWasm.Time.setTimeInterval(CooWasm.EnumTimeIntervalType.SECOND); // 设置回调间隔
     * CooWasm.Time.pause();                     // 暂停时间
     * CooWasm.Time.resume();                    // 继续时间
     * CooWasm.Time.reset();                     // 重置时间
     * var currentTime = CooWasm.Time.getTime(); // 获取当前时间
     */
    class TimeSingleton {
        /**
         * @description Time在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Time"

        constructor() {
            callbackSystem.register("", "EngineTime", (time) => {
                if (defined(Time.callback)) {
                    Time.callback(time)
                }
            })
        }
    }
    return Time
})